import { jsPDF } from 'jspdf'

const TEAL = [27, 110, 107]
const TEXT = [26, 26, 26]
const MUTED = [107, 111, 110]
const AMBER = [212, 136, 14]

const fmtDate = (iso) => (iso ? new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : '—')
const fmtShort = (iso) => (iso ? new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—')

export function generateDoctorSummaryPDF(recipient, s) {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' })
  const M = 56
  const W = doc.internal.pageSize.getWidth()
  const H = doc.internal.pageSize.getHeight()
  let y = M

  const ensure = (needed) => {
    if (y + needed > H - 56) { doc.addPage(); y = M }
  }
  const heading = (t) => {
    ensure(40)
    y += 14
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(...TEAL)
    doc.text(t, M, y)
    y += 6
    doc.setDrawColor(...TEAL); doc.setLineWidth(1); doc.line(M, y, W - M, y)
    y += 12
  }
  const line = (label, value, color = TEXT) => {
    ensure(18)
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...MUTED)
    doc.text(label, M, y)
    doc.setFont('helvetica', 'normal'); doc.setFontSize(11); doc.setTextColor(...color)
    const lines = doc.splitTextToSize(String(value), W - M - M - 150)
    doc.text(lines, M + 150, y)
    y += Math.max(16, lines.length * 14)
  }
  const bullet = (text, color = TEXT) => {
    const lines = doc.splitTextToSize(String(text), W - M - M - 14)
    ensure(lines.length * 14 + 2)
    doc.setFont('helvetica', 'normal'); doc.setFontSize(11); doc.setTextColor(...color)
    doc.text('•', M, y)
    doc.text(lines, M + 14, y)
    y += lines.length * 14 + 2
  }

  // Header
  doc.setFillColor(...TEAL); doc.rect(0, 0, W, 8, 'F')
  y = M + 4
  doc.setFont('helvetica', 'bold'); doc.setFontSize(22); doc.setTextColor(...TEAL)
  doc.text('30-Day Care Summary', M, y); y += 18
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(...MUTED)
  doc.text(`Mapa Care  ·  Generated ${s.generatedAt}`, M, y); y += 6

  // Patient
  heading('Care recipient')
  line('Name', recipient.full_name || '—')
  line('Date of birth', recipient.date_of_birth ? fmtDate(recipient.date_of_birth) : '—')
  line('Care stage', (recipient.care_stage || 'unknown'))
  line('Conditions', (recipient.conditions || []).join(', ') || 'None recorded')
  line('Allergies', (recipient.allergies || []).join(', ') || 'None recorded', (recipient.allergies || []).length ? AMBER : TEXT)

  // Flagged concerns first (clinically important)
  if (s.flaggedConcerns.length) {
    heading('Flagged concerns (behaviours 3+ times in 30 days)')
    s.flaggedConcerns.forEach((c) => bullet(`${c.label}: ${c.count} occurrences`, AMBER))
  }

  // Medications
  heading('Active medications')
  if (s.medications.length === 0) bullet('None recorded.')
  else s.medications.forEach((m) => bullet(`${m.name}${m.dose ? ` ${m.dose}` : ''}${m.timing ? ` — ${m.timing}` : ''}${m.purpose ? ` (${m.purpose})` : ''}`))

  // Adherence
  heading('Medication adherence (last 30 days)')
  if (s.adherence.length === 0) bullet('No medication logs recorded.')
  else s.adherence.forEach((a) => line(a.name, `Taken ${a.taken}  ·  Missed ${a.missed}  ·  Refused ${a.refused}`))

  // Mood
  heading('Mood trend (weekly average, 1–5)')
  if (s.moodWeekly.length === 0) bullet('No mood entries recorded.')
  else s.moodWeekly.forEach((w) => line(w.week, `${w.avg}/5  (${w.n} entr${w.n === 1 ? 'y' : 'ies'})`))

  // Behaviours
  heading('Behaviours flagged (last 30 days)')
  if (s.behaviourCounts.length === 0) bullet('None recorded.')
  else s.behaviourCounts.forEach((b) => line(b.label, `${b.count} time${b.count === 1 ? '' : 's'}`, b.count >= 3 ? AMBER : TEXT))

  // Appointments
  heading('Appointments (last 30 days)')
  if (s.appointments.length === 0) bullet('None in this period.')
  else s.appointments.forEach((a) => {
    bullet(`${fmtShort(a.appointment_date)} — ${a.doctor_name}${a.purpose ? `: ${a.purpose}` : ''}`)
    if (a.action_items) { doc.setTextColor(...MUTED); doc.setFontSize(10); const l = doc.splitTextToSize(`Action: ${a.action_items}`, W - M - M - 28); ensure(l.length * 12); doc.text(l, M + 28, y); y += l.length * 12 + 2 }
  })

  // Footer on each page
  const pages = doc.internal.getNumberOfPages()
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p)
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...MUTED)
    doc.text(`Mapa Care · ${recipient.full_name || ''} · Page ${p} of ${pages}`, M, H - 32)
  }

  const safe = (recipient.full_name || 'care').replace(/[^a-z0-9]+/gi, '-').toLowerCase()
  doc.save(`${safe}-30-day-summary.pdf`)
}
